// src/pages/Plantation/tabs/VehiculesTab.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Truck, Edit2, Trash2, Plus, RefreshCw, X } from 'lucide-react';
import { plantationService, PlantationVehicule } from '../../../services/plantation.service';

interface Props {
  entiteId: string;
}

const TYPE_LABEL: Record<PlantationVehicule['type'], string> = {
  camion:    'Camion',
  'pick-up': 'Pick-up',
  tracteur:  'Tracteur',
  benne:     'Benne',
  citerne:   'Citerne',
  autre:     'Autre',
};

const TYPE_BADGE: Record<PlantationVehicule['type'], string> = {
  camion:    'bg-blue-100 text-blue-700',
  'pick-up': 'bg-sky-100 text-sky-700',
  tracteur:  'bg-orange-100 text-orange-700',
  benne:     'bg-purple-100 text-purple-700',
  citerne:   'bg-teal-100 text-teal-700',
  autre:     'bg-gray-100 text-gray-600',
};

const STATUT_BADGE: Record<PlantationVehicule['statut'], string> = {
  actif:          'bg-emerald-100 text-emerald-700',
  en_maintenance: 'bg-amber-100 text-amber-700',
  hors_service:   'bg-red-100 text-red-700',
};

const STATUT_LABEL: Record<PlantationVehicule['statut'], string> = {
  actif:          'Actif',
  en_maintenance: 'En maintenance',
  hors_service:   'Hors service',
};

const EMPTY_FORM: Partial<PlantationVehicule> = {
  immatriculation: '',
  marque: '',
  modele: '',
  type: 'camion',
  capaciteChargeKg: undefined,
  annee: undefined,
  statut: 'actif',
  chauffeurMatricule: '',
  observations: '',
};

const VehiculesTab: React.FC<Props> = ({ entiteId }) => {
  const [vehicles, setVehicles] = useState<PlantationVehicule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<PlantationVehicule | null>(null);
  const [form, setForm] = useState<Partial<PlantationVehicule>>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!entiteId) return;
    setLoading(true);
    setError('');
    try {
      const data = await plantationService.listVehicules(entiteId);
      setVehicles(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erreur lors du chargement des véhicules');
    } finally {
      setLoading(false);
    }
  }, [entiteId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (v: PlantationVehicule) => {
    setEditing(v);
    setForm({ ...v });
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.immatriculation?.trim()) {
      setError("L'immatriculation est obligatoire.");
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await plantationService.updateVehicule(editing.id, { ...form, entiteId });
      } else {
        await plantationService.createVehicule({ ...form, entiteId });
      }
      await load();
      closeModal();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (v: PlantationVehicule) => {
    if (!window.confirm(`Supprimer le véhicule ${v.immatriculation} ?`)) return;
    setError('');
    try {
      await plantationService.deleteVehicule(v.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const set = (k: keyof PlantationVehicule, v: any) => setForm(f => ({ ...f, [k]: v }));

  // KPIs
  const total = vehicles.length;
  const actifs = vehicles.filter(v => v.statut === 'actif').length;
  const enMaintenance = vehicles.filter(v => v.statut === 'en_maintenance').length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-emerald-600" />
          <h2 className="text-base font-semibold text-gray-800">Référentiel des véhicules</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"
            title="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openCreate}
            className="bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-emerald-700 flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> Nouveau véhicule
          </button>
        </div>
      </div>

      {/* Erreur globale */}
      {error && !showModal && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-start gap-2">
          <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
            <Truck className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Total véhicules</p>
            <p className="text-lg font-bold text-gray-800">{total}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lime-100 text-lime-700 flex items-center justify-center flex-shrink-0">
            <Truck className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Actifs</p>
            <p className="text-lg font-bold text-gray-800">{actifs}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Truck className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-gray-500">En maintenance</p>
            <p className="text-lg font-bold text-gray-800">{enMaintenance}</p>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                {['Immatriculation', 'Marque / Modèle', 'Type', 'Charge max', 'Chauffeur attitré', 'Statut', 'Actions'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400">Chargement…</td></tr>
              ) : vehicles.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-gray-400">
                    <Truck className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    Aucun véhicule enregistré
                  </td>
                </tr>
              ) : vehicles.map(v => (
                <tr key={v.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 font-mono font-semibold text-gray-800">{v.immatriculation}</td>
                  <td className="px-3 py-2.5 text-gray-700">
                    {[v.marque, v.modele].filter(Boolean).join(' ') || '—'}
                    {v.annee && <span className="ml-1 text-xs text-gray-400">({v.annee})</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${TYPE_BADGE[v.type] || 'bg-gray-100 text-gray-600'}`}>
                      {TYPE_LABEL[v.type] || v.type}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-700">
                    {v.capaciteChargeKg != null ? `${v.capaciteChargeKg.toLocaleString('fr-FR')} kg` : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 text-xs font-mono">
                    {v.chauffeurMatricule || '—'}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${STATUT_BADGE[v.statut] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUT_LABEL[v.statut] || v.statut}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(v)}
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Modifier"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(v)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal création / édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                {editing ? 'Modifier le véhicule' : 'Nouveau véhicule'}
              </h3>
              <button onClick={closeModal} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-4">
              {/* Erreur dans la modal */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2.5 text-sm">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Immatriculation *</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                    value={form.immatriculation || ''}
                    onChange={e => set('immatriculation', e.target.value)}
                    placeholder="ex: CI-1234-AB"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.type || 'camion'}
                    onChange={e => set('type', e.target.value)}
                  >
                    {(Object.entries(TYPE_LABEL) as [PlantationVehicule['type'], string][]).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Marque</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.marque || ''}
                    onChange={e => set('marque', e.target.value)}
                    placeholder="ex: Toyota, Mercedes…"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Modèle</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.modele || ''}
                    onChange={e => set('modele', e.target.value)}
                    placeholder="ex: Hilux, Actros…"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Capacité de charge (kg)</label>
                  <input
                    type="number"
                    min={0}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.capaciteChargeKg ?? ''}
                    onChange={e => set('capaciteChargeKg', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Année</label>
                  <input
                    type="number"
                    min={2000}
                    max={2030}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.annee ?? ''}
                    onChange={e => set('annee', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Chauffeur attitré — matricule</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                    value={form.chauffeurMatricule || ''}
                    onChange={e => set('chauffeurMatricule', e.target.value)}
                    placeholder="ex: EMP-0042"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.statut || 'actif'}
                    onChange={e => set('statut', e.target.value)}
                  >
                    <option value="actif">Actif</option>
                    <option value="en_maintenance">En maintenance</option>
                    <option value="hors_service">Hors service</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Observations</label>
                <textarea
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  value={form.observations || ''}
                  onChange={e => set('observations', e.target.value)}
                />
              </div>
            </div>

            <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? 'Enregistrement…' : editing ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiculesTab;
